import { CONFIG, authHeaders, authFetch } from './config';

export class QRCodeService {
  static MINIATURE_CONFIGS = {
    LIST: { miniature: 'true', miniature_quality: 70, miniature_size: 64 },
    PREVIEW: { miniature: 'true', miniature_quality: 80, miniature_size: 128 },
    EDITOR: { miniature: 'true', miniature_quality: 80, miniature_size: 256 },
    HIGH_QUALITY: { miniature: 'true', miniature_quality: 90, miniature_size: 512 }
  };
  static async getAllQRCodes(params = {}) {
    try {
      const defaultParams = {
        ...QRCodeService.MINIATURE_CONFIGS.LIST,
        ...params
      };
      
      const queryParams = new URLSearchParams(defaultParams).toString();
      const url = `${CONFIG.BASE_URL}/qr-code${queryParams ? `?${queryParams}` : ''}`;
      
      const response = await authFetch(url, {
        method: 'GET',
        headers: authHeaders({
          'Content-Type': 'application/json',
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération des QR codes:', error);
      throw error;
    }

  }

  static async getQRCodeById(uuid, params = {}) {
    try {
      const defaultParams = {
        ...QRCodeService.MINIATURE_CONFIGS.PREVIEW,
        ...params
      };
      
      const queryParams = new URLSearchParams(defaultParams).toString();
      const url = `${CONFIG.BASE_URL}/qr-code/${uuid}${queryParams ? `?${queryParams}` : ''}`;
      
      const response = await authFetch(url, {
        method: 'GET',
        headers: authHeaders({
          'Content-Type': 'application/json',
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération du QR code:', error);
      throw error;
    }
  }

  static async submitQRCodeStylizedJob(payload, workflow = 'simple') {
    const formData = new FormData();
    formData.append('data', JSON.stringify(payload));

    const response = await authFetch(`${CONFIG.BASE_URL}/qr-code-stylized-async?workflow=${workflow}`, {
      method: 'POST',
      headers: authHeaders(false),
      body: formData,
    });

    if (!response.ok) {
      const error = new Error();
      error.status = response.status;
      try {
        const errorJson = await response.json();
        error.message = errorJson.error || JSON.stringify(errorJson);
      } catch {
        error.message = await response.text();
      }
      throw error;
    }

    return response.json();
  }

  static async pollQRCodeStylizedJob(jobId, { intervalMs = 3000, timeoutMs = 600000 } = {}) {
    const deadline = Date.now() + timeoutMs;

    while (true) {
      const response = await authFetch(`${CONFIG.BASE_URL}/qr-code-stylized-async/${jobId}`, {
        method: 'GET',
        headers: authHeaders(),
      });

      if (response.status === 200) {
        return response.json();
      }

      if (response.status !== 202) {
        const error = new Error();
        error.status = response.status;
        try {
          const errorJson = await response.json();
          error.message = errorJson.error || JSON.stringify(errorJson);
        } catch {
          error.message = await response.text();
        }
        throw error;
      }

      if (Date.now() >= deadline) {
        const error = new Error('Generation job timed out');
        error.status = 'timeout';
        throw error;
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  static async getAllQRCodeStats(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `${CONFIG.BASE_URL}/qr-code/stats${queryParams ? `?${queryParams}` : ''}`;

      const response = await authFetch(url, {
        method: 'GET',
        headers: authHeaders({
          'Content-Type': 'application/json',
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const rawData = await response.json();
      const visits = rawData?.visits?.data || [];
      const pagination = rawData?.visits?.pagination || {};

      const now = new Date();
      const DAY_MS = 24 * 60 * 60 * 1000;
      const total_visits = pagination.totalItems ?? visits.length;

      let visits_this_week = 0;
      const perUrlStats = {};

      visits.forEach((v) => {
        const date = new Date(v.date || v.visitedAt || v.visited_at);
        const diffDays = Math.floor((now - date) / DAY_MS);

        if (diffDays >= 0 && diffDays < 7) visits_this_week++;

        const key = v.visitedUrl || '';
        if (!perUrlStats[key]) {
          perUrlStats[key] = { total_visits: 0, visits_this_week: 0 };
        }
        perUrlStats[key].total_visits++;
        if (diffDays >= 0 && diffDays < 7) perUrlStats[key].visits_this_week++;
      });

      return { total_visits, visits_this_week, perUrlStats };
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques globales:', error);
      throw error;
    }
  }

  static async getQRCodeStats(uuid, params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `${CONFIG.BASE_URL}/qr-code/${uuid}/stats${queryParams ? `?${queryParams}` : ''}`;

      const response = await authFetch(url, {
        method: 'GET',
        headers: authHeaders({
          'Content-Type': 'application/json',
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const rawData = await response.json();

      let visits = [];
      visits = rawData.visits.data;
      
      const pagination = rawData?.visits?.pagination || {};

      const now = new Date();
      const DAY_MS = 24 * 60 * 60 * 1000;

      const total_visits = pagination.totalItems ?? visits.length;

      let visits_this_week = 0;
      let visits_this_month = 0;

      const dailyVisits = Array(7).fill(0);
      const monthlyVisits = Array(12).fill(0);
      const yearlyVisits = {};
      const countryCounts = {};
      const referrerCounts = {};
      const deviceCounts = { mobile: 0, desktop: 0, tablet: 0 };
      const uniqueVisitors = new Set();

      const detectDevice = (ua = '') => {
        const l = ua.toLowerCase();
        if (/tablet|ipad/.test(l)) return 'tablet';
        if (/mobi|android/.test(l)) return 'mobile';
        return 'desktop';
      };

      const extractDomain = (ref) => {
        if (!ref) return 'Direct';
        try {
          return new URL(ref).hostname.replace(/^www\./, '');
        } catch (_) {
          return 'Direct';
        }
      };

      visits.forEach((v) => {
        const visitedAtStr = v.visitedAt || v.date || v.visited_at;
        const visitedAt = new Date(visitedAtStr);
        const diffDays = Math.floor((now - visitedAt) / DAY_MS);

        if (diffDays < 7) visits_this_week += 1;
        if (diffDays < 30) visits_this_month += 1;

        if (diffDays >= 0 && diffDays < 7) {
          dailyVisits[6 - diffDays] += 1;
        }

        const monthsDiff = (now.getFullYear() - visitedAt.getFullYear()) * 12 + (now.getMonth() - visitedAt.getMonth());
        if (monthsDiff >= 0 && monthsDiff < 12) {
          monthlyVisits[11 - monthsDiff] += 1;
        }

        const year = visitedAt.getFullYear();
        yearlyVisits[year] = (yearlyVisits[year] || 0) + 1;

        const country = v.visitLocation?.countryName || v.location?.countryName || 'Inconnu';
        countryCounts[country] = (countryCounts[country] || 0) + 1;

        const domain = extractDomain(v.referer);
        referrerCounts[domain] = (referrerCounts[domain] || 0) + 1;

        const deviceType = detectDevice(v.userAgent || '');
        deviceCounts[deviceType] += 1;

        const uniqueKey = `${v.visitLocation?.ipAddress || v.ipAddress || ''}_${v.userAgent || ''}`;
        uniqueVisitors.add(uniqueKey);
      });

      const countries = Object.entries(countryCounts)
        .map(([name, visits]) => ({ name, visits }))
        .sort((a, b) => b.visits - a.visits);

      const referrers = Object.entries(referrerCounts)
        .map(([domain, visits]) => ({ domain, visits }))
        .sort((a, b) => b.visits - a.visits);

      const totalDevice = deviceCounts.mobile + deviceCounts.desktop + deviceCounts.tablet || 1;
      const devices = {
        mobile: Math.round((deviceCounts.mobile / totalDevice) * 100),
        desktop: Math.round((deviceCounts.desktop / totalDevice) * 100),
        tablet: Math.round((deviceCounts.tablet / totalDevice) * 100),
      };

      const recent_visits = visits
        .slice()
        .sort((a, b) => new Date(b.visitedAt || b.date) - new Date(a.visitedAt || a.date))
        .slice(0, 10)
        .map((v) => ({
          timestamp: v.visitedAt || v.date || new Date().toISOString(),
          country: v.visitLocation?.countryName || v.location?.countryName || 'Inconnu',
          device: detectDevice(v.userAgent || ''),
        }));

      const sortedYears = Object.keys(yearlyVisits).sort((a, b) => parseInt(a) - parseInt(b));
      const yearlyVisitsArray = sortedYears.map(year => yearlyVisits[year]);

      return {
        total_visits,
        visits_this_week,
        visits_this_month,
        unique_visitors: uniqueVisitors.size,
        daily_visits: dailyVisits,
        monthly_visits: monthlyVisits,
        yearly_visits: yearlyVisitsArray,
        countries,
        referrers,
        devices,
        recent_visits,
        visits,
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw error;
    }
  }

  static formatDate(dateString) {
    if (!dateString) return 'N/A';
    
    try {
      let dateStr = dateString.toString();
      
      if (dateStr.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
        dateStr = dateStr.replace(' ', 'T');
      }
      
      const date = new Date(dateStr);
      
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }
      
      const offsetMinutes = date.getTimezoneOffset();
      const localDate = new Date(date.getTime() + (offsetMinutes * 60 * 1000));
      
      return localDate.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Date invalide';
    }
  }

  static formatDateTime(dateString) {
    if (!dateString) return 'N/A';
    
    try {
      let dateStr = dateString.toString();
      
      if (dateStr.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
        dateStr = dateStr.replace(' ', 'T');
      }
      
      const date = new Date(dateStr);
      
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }
      
      const offsetMinutes = date.getTimezoneOffset();
      const localDate = new Date(date.getTime() + (offsetMinutes * 60 * 1000));
      
      return localDate.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Date invalide';
    }
  }

  static formatDateOnly(dateString) {
    if (!dateString) return 'N/A';
    
    try {
      let dateStr = dateString.toString();
      
      if (dateStr.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
        dateStr = dateStr.replace(' ', 'T');
      }
      
      const date = new Date(dateStr);
      
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }
      
      const offsetMinutes = date.getTimezoneOffset();
      const localDate = new Date(date.getTime() + (offsetMinutes * 60 * 1000));
      
      return localDate.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Date invalide';
    }
  }

  static truncateUrl(url, maxLength = 50) {
    if (!url) return 'N/A';
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  }

  static getQRCodeTypeLabel(type) {
    const types = {
      'url': 'URL',
      'text': 'Texte',
      'email': 'Email',
      'phone': 'Téléphone',
      'sms': 'SMS',
      'wifi': 'WiFi',
      'vcard': 'vCard',
      'gs1': 'GS1 Digital Link'
    };
    return types[type] || 'Autre';
  }

  static async generateQRThumbnail(qrCode, size = 128) {
    try {
      const inlineMiniature = qrCode.miniature || qrCode.mignature;
      if (inlineMiniature) {
        return `data:image/webp;base64,${inlineMiniature}`;
      }
      
      if (qrCode.uuid) {
        const response = await authFetch(`${CONFIG.BASE_URL}/qr-code/${qrCode.uuid}/raw-image`, {
          method: 'GET',
          headers: authHeaders()
        });

        if (response.ok) {
          const base64Data = await response.text();
          return `data:image/png;base64,${base64Data}`;
        }
      }

      const requestData = {
        'redirect_url': qrCode.redirect_url || qrCode.url,
        'content': qrCode.redirect_url || qrCode.url,
        'retrieve-mode': 'base64',
        'size': size,
        'dots-color': '#000000',
        'background-color': '#ffffff'
      };

      const formData = new FormData();
      formData.append('data', JSON.stringify(requestData));

      const response = await authFetch(`${CONFIG.BASE_URL}/preview-classic-qr-code`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Erreur API ${response.status}`);
      }

      const result = await response.json();
      
      if (result['qr-code-data']) {
        return `data:image/png;base64,${result['qr-code-data']}`;
      } else {
        throw new Error('Données QR manquantes');
      }
    } catch (error) {
      console.error('Erreur génération miniature QR:', error);
      return null;
    }
  }

  static getQRThumbnailUrl(qrCode) {
    const inlineMiniature = qrCode.miniature || qrCode.mignature;
    if (inlineMiniature) {
      return `data:image/webp;base64,${inlineMiniature}`;
    }
    return null;
  }

  static async getQRCodeKinds(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `${CONFIG.BASE_URL}/qr-code-kinds${queryParams ? `?${queryParams}` : ''}`;
      
      const response = await authFetch(url, {
        method: 'GET',
        headers: authHeaders({
          'Content-Type': 'application/json',
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération des types de QR codes:', error);
      throw error;
    }
  }
}
